// PreviewTester/components/LeftPanel.js
import React, { useEffect, useRef, useState } from 'react';
import styles from './LeftPanel.module.css';
import {
  generateTemplateFromJSON,
  generateCase3Template,
  detectNestedArrays,
} from '../utils/templateGenerator';
import LayoutChoicePopup from './LayoutChoicePopup';
import TemplateEditor from '../../PreviewEditorHome/TemplateEditor';
import { TemplateValidator } from '../utils/templateValidator';
import {
  isButtonStep,
  isButtonGroupStep,
  isStyleStep,
  isStructuralStep,
  isMediaStep,
  isCombinedStep,
} from '../utils/transformationSchema';

const DEFAULT_TEMPLATE = '{{ response_data | dump | safe }}';

// ✅ UX: Hide the raw template (Advanced) editor on production.
// Dev / staging continue to see it for debugging.
const IS_PROD =
  process.env.REACT_APP_STAGE_ENV === 'prod' ||
  process.env.NODE_ENV === 'production';

// Helper to count non-empty, non-template rows
const countContentRows = (template) => {
  if (!template) return 0;
  return template.split('\n')
    .filter(line => {
      const trimmed = line.trim();
      return trimmed.length > 0 &&
        !trimmed.startsWith('{%') &&
        !trimmed.startsWith('{#');
    })
    .length;
};

// ✅ UPDATED: Generate a selector path for a content row at a given index
// Supports both old and new selector formats
const generateSelectorPathForLine = (templateLines, rowIndex) => {
  if (!templateLines || rowIndex < 0 || rowIndex >= templateLines.length) return '';

  // Build path: div:nth-child(1) > div:nth-child(1) > div:nth-child(N)
  const pathParts = ['div:nth-child(1)', 'div:nth-child(1)'];

  // Count how many content rows before this one (at the same depth level)
  let targetPos = 1;
  for (let i = 0; i < rowIndex; i++) {
    const line = templateLines[i];
    if (line && line.trim().length > 0) {
      const trimmed = line.trim();
      const isTemplateTag = trimmed.startsWith('{%') || trimmed.startsWith('{#');
      if (!isTemplateTag) {
        targetPos++;
      }
    }
  }

  pathParts.push(`div:nth-child(${targetPos})`);
  return pathParts.join(' > ');
};

const LeftPanel = ({
  sampleResponse,
  setSampleResponse,
  template,
  setTemplate,
  error,
  handleRender,
  parsedSample,
  flattenedFields,
  insertField,
  // Edit Mode props
  isEditMode = false,
  setIsEditMode = () => { },
  templateLines = [],
  setTemplateLines = () => { },
  availableVariables = [],
  onTemplateUpdate = () => { },
  onTrackChange = () => { },
  // Disable state
  isDisabled = false,
  // Reset props
  onResetAll = () => { },
  transformationRules = [],
  // Wizard state
  showPhaseWizard = false,
  parseTemplateForEdit,
  isCase2,
  // Validation props
  validation = { valid: true, errors: [], warnings: [] },
  onOpenEditor = null,
  onCloseEditor = null,
  hasPreviewData = false,
  onAddTransformationStep = () => { },
  onRemoveTransformationStep = () => { },
  onUpdateTransformationStep = () => { },
  // === STEP 3a: Layout choice (from parent) ===
  userScreenChoice = null,
  setUserScreenChoice = () => { },
  // ✅ UX: Collapse / expand support
  isCollapsed = false,
  onToggleCollapse = () => { },
}) => {
  const wasAutoGeneratedRef = useRef(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [pendingSampleValue, setPendingSampleValue] = useState(null);

  // ✅ Layout choice popup state (for nested arrays)
  const [showLayoutChoicePopup, setShowLayoutChoicePopup] = useState(false);
  const [pendingParsedResponse, setPendingParsedResponse] = useState(null);
  const [detectedArraysForPopup, setDetectedArraysForPopup] = useState(null);

  // === STEP 3b: Info banner shown when user cancels the layout popup ===
  const [layoutCancelNotice, setLayoutCancelNotice] = useState(false);

  // Track previous template state for change detection
  const previousTemplateRef = useRef(template);
  const previousRowCountRef = useRef(countContentRows(template));

  // ✅ Track previous sampleResponse prop to detect external changes
  const previousSampleResponseRef = useRef(null);

  const [originalTemplate, setOriginalTemplate] = useState(template);
  const hasTemplateChanges = template !== originalTemplate && !isEditMode;

  useEffect(() => {
    if (!isEditMode && template) {
      setOriginalTemplate(template);
    }
  }, [template, isEditMode]);

  const templateString = typeof template === 'string' ? template : '';

  // ============================================================
  // ✅ SINGLE SOURCE OF TRUTH:
  // React to sampleResponse prop changes and run detection.
  //
  // Flow:
  //   1. If nothing changed → do nothing.
  //   2. If template is NOT default (real template exists, e.g. loaded
  //      from DB or built previously) → skip detection entirely.
  //   3. Otherwise → run detection via applySampleResponse, which
  //      will either auto-generate (Case 1/2) or show the layout popup
  //      (Case 3 — object with nested arrays).
  // ============================================================
  useEffect(() => {
    if (!sampleResponse) return;

    const isFirstLoad = previousSampleResponseRef.current === null;
    const hasChanged = sampleResponse !== previousSampleResponseRef.current;

    // Nothing changed → nothing to do
    if (!isFirstLoad && !hasChanged) return;

    // ✅ Guard: if a real template already exists, don't run detection.
    const currentTemplate = typeof template === 'string' ? template.trim() : '';
    const isDefaultTemplate =
      currentTemplate === DEFAULT_TEMPLATE || currentTemplate === '';

    if (!isDefaultTemplate) {
      console.log(
        '[LeftPanel] ⏭️ Template already exists — skipping detection.'
      );
      previousSampleResponseRef.current = sampleResponse;
      return;
    }

    console.log(
      '[LeftPanel] 📥 Sample response changed (firstLoad:',
      isFirstLoad,
      ') — running detection.'
    );
    previousSampleResponseRef.current = sampleResponse;
    applySampleResponse(sampleResponse);
  }, [sampleResponse, template]);

  const handleSampleResponseChange = (e) => {
    const value = e.target.value;
    const hasExistingContent = sampleResponse && sampleResponse.trim().length > 0;
    const hasWorkDone = transformationRules.length > 0 || hasTemplateChanges || (isEditMode && templateLines.length > 0);

    if (hasExistingContent && hasWorkDone && value.trim().length > 0) {
      setPendingSampleValue(value);
      setShowResetConfirm(true);
      return;
    }

    applySampleResponse(value);
  };

  // ============================================================
  // Layout Choice Popup Handlers
  // ============================================================

  const handleConfirmSingleScreen = () => {
    console.log('[LeftPanel] 📄 User confirmed Single Screen');

    setUserScreenChoice('single');
    setShowLayoutChoicePopup(false);

    if (pendingParsedResponse) {
      const generatedTemplate = generateTemplateFromJSON(pendingParsedResponse);

      if (
        generatedTemplate &&
        typeof generatedTemplate === 'string' &&
        generatedTemplate.trim()
      ) {
        setTemplate(generatedTemplate);
        wasAutoGeneratedRef.current = true;
        previousTemplateRef.current = generatedTemplate;
        previousRowCountRef.current = countContentRows(generatedTemplate);

        // ✅ FIX: Re-parse templateLines against the new template
        if (parsedSample && parseTemplateForEdit) {
          const editLines = parseTemplateForEdit(
            generatedTemplate,
            parsedSample,
            isCase2,
            null
          );
          setTemplateLines(editLines);
          console.log(
            '[LeftPanel] ✅ Re-parsed templateLines after Single Screen choice:',
            editLines.length
          );
        }
      } else {
        setTemplate(DEFAULT_TEMPLATE);
        previousTemplateRef.current = DEFAULT_TEMPLATE;
        previousRowCountRef.current = countContentRows(DEFAULT_TEMPLATE);
      }
    }

    setPendingParsedResponse(null);
    setDetectedArraysForPopup(null);
  };


  const handleConfirmMultipleScreens = (selectedArrayPath) => {
    console.log(
      '[LeftPanel] 📚 User confirmed Multiple Screens with array:',
      selectedArrayPath
    );

    setUserScreenChoice('multiple');
    setShowLayoutChoicePopup(false);

    if (pendingParsedResponse && selectedArrayPath) {
      const generatedTemplate = generateCase3Template(
        pendingParsedResponse,
        selectedArrayPath
      );

      if (
        generatedTemplate &&
        typeof generatedTemplate === 'string' &&
        generatedTemplate.trim()
      ) {
        setTemplate(generatedTemplate);
        wasAutoGeneratedRef.current = true;
        previousTemplateRef.current = generatedTemplate;
        previousRowCountRef.current = countContentRows(generatedTemplate);

        // ✅ FIX: Re-parse templateLines against the new template
        if (parsedSample && parseTemplateForEdit) {
          const editLines = parseTemplateForEdit(
            generatedTemplate,
            parsedSample,
            isCase2,
            null
          );
          setTemplateLines(editLines);
          console.log(
            '[LeftPanel] ✅ Re-parsed templateLines after Multiple Screens choice:',
            editLines.length
          );
        }
      } else {
        setTemplate(DEFAULT_TEMPLATE);
        previousTemplateRef.current = DEFAULT_TEMPLATE;
        previousRowCountRef.current = countContentRows(DEFAULT_TEMPLATE);
      }
    }

    setPendingParsedResponse(null);
    setDetectedArraysForPopup(null);
  };


  const handleCloseLayoutPopup = () => {
    console.log(
      '[LeftPanel] ❌ Layout popup cancelled — defaulting to Single Screen'
    );

    setUserScreenChoice('single');
    setShowLayoutChoicePopup(false);
    setPendingParsedResponse(null);
    setDetectedArraysForPopup(null);

    setTemplate(DEFAULT_TEMPLATE);
    previousTemplateRef.current = DEFAULT_TEMPLATE;
    previousRowCountRef.current = countContentRows(DEFAULT_TEMPLATE);

    // ✅ FIX: Re-parse templateLines against default template
    if (parsedSample && parseTemplateForEdit) {
      const editLines = parseTemplateForEdit(
        DEFAULT_TEMPLATE,
        parsedSample,
        isCase2,
        null
      );
      setTemplateLines(editLines);
    }

    setLayoutCancelNotice(true);
    setTimeout(() => setLayoutCancelNotice(false), 6000);
  };


  const applySampleResponse = (value) => {
    // ✅ Guard: only update the prop if it's actually different
    if (value !== sampleResponse) {
      setSampleResponse(value);
    }
    setShowResetConfirm(false);
    setPendingSampleValue(null);

    if (value.trim()) {
      try {
        const parsed = JSON.parse(value);

        // ============================================================
        // PRIMITIVE BRANCH: null / undefined / string / number / boolean
        // ============================================================
        if (
          parsed === null ||
          parsed === undefined ||
          typeof parsed === 'string' ||
          typeof parsed === 'number' ||
          typeof parsed === 'boolean'
        ) {
          if (parsed === null || parsed === undefined) {
            // null → unclassified → leave DEFAULT
            console.log('[LeftPanel] ⏭️ Null/undefined response — keeping DEFAULT');
            setTemplate(DEFAULT_TEMPLATE);
            previousTemplateRef.current = DEFAULT_TEMPLATE;
            previousRowCountRef.current = countContentRows(DEFAULT_TEMPLATE);
          } else {
            // String / number / boolean → use the literal value as the template
            const literal = String(parsed);
            console.log('[LeftPanel] 🔤 Primitive response — using literal template:', literal);
            setTemplate(literal);
            wasAutoGeneratedRef.current = true;
            previousTemplateRef.current = literal;
            previousRowCountRef.current = countContentRows(literal);
          }
        }
        // ============================================================
        // OBJECT / ARRAY BRANCH
        // ============================================================
        else if (typeof parsed === 'object') {
          const detectedArrays = detectNestedArrays(parsed);
          const nestedArrays = detectedArrays.filter((a) => a.isNested);

          console.log('[LeftPanel] 📊 Detected arrays:', detectedArrays);
          console.log('[LeftPanel] 📊 Nested arrays:', nestedArrays);

          // CASE A: Root is an array → auto-generate Case 1 template
          if (Array.isArray(parsed)) {
            const generatedTemplate = generateTemplateFromJSON(parsed);
            if (
              generatedTemplate &&
              typeof generatedTemplate === 'string' &&
              generatedTemplate.trim()
            ) {
              setTemplate(generatedTemplate);
              wasAutoGeneratedRef.current = true;
              previousTemplateRef.current = generatedTemplate;
              previousRowCountRef.current = countContentRows(generatedTemplate);
            } else {
              setTemplate(DEFAULT_TEMPLATE);
              previousTemplateRef.current = DEFAULT_TEMPLATE;
              previousRowCountRef.current = countContentRows(DEFAULT_TEMPLATE);
            }
          }
          // CASE B: Object WITH nested arrays → show popup
          // CASE B: Object WITH any array (top-level or nested) → show popup
          else if (detectedArrays.length > 0) {
            console.log(
              '[LeftPanel] 🔔 Array(s) detected — showing layout choice popup',
              'arrays:',
              detectedArrays.length
            );
            setPendingParsedResponse(parsed);
            setDetectedArraysForPopup({
              allArrays: detectedArrays,
              nestedArrays: nestedArrays,
            });
            setShowLayoutChoicePopup(true);
            return; // Wait for user choice
          }
          // CASE C: Object with NO nested arrays → auto-generate Case 2 template
          else {
            const generatedTemplate = generateTemplateFromJSON(parsed);
            if (
              generatedTemplate &&
              typeof generatedTemplate === 'string' &&
              generatedTemplate.trim()
            ) {
              setTemplate(generatedTemplate);
              wasAutoGeneratedRef.current = true;
              previousTemplateRef.current = generatedTemplate;
              previousRowCountRef.current = countContentRows(generatedTemplate);
            } else {
              setTemplate(DEFAULT_TEMPLATE);
              previousTemplateRef.current = DEFAULT_TEMPLATE;
              previousRowCountRef.current = countContentRows(DEFAULT_TEMPLATE);
            }
          }
        }
      } catch (err) {
        // Invalid JSON with non-empty value → keep existing template
        console.log('[LeftPanel] Invalid JSON, keeping existing template');
      }
    } else {
      // Empty value → reset to DEFAULT
      setTemplate(DEFAULT_TEMPLATE);
      previousTemplateRef.current = DEFAULT_TEMPLATE;
      previousRowCountRef.current = countContentRows(DEFAULT_TEMPLATE);
    }

    if (onResetAll) {
      onResetAll();
    }
  };

  // ============================================================
  // ✅ FIXED: Reset now flows through the useEffect (single source
  // of truth). We just:
  //   1. Reset the template to DEFAULT so the guard passes.
  //   2. Update the sampleResponse prop.
  //   3. The useEffect picks up the change and runs detection.
  // ============================================================
  const handleConfirmReset = () => {
    setShowResetConfirm(false);

    if (pendingSampleValue !== null) {
      // ✅ Reset template to DEFAULT so the detection useEffect's
      //    isDefaultTemplate guard passes cleanly.
      setTemplate(DEFAULT_TEMPLATE);
      previousTemplateRef.current = DEFAULT_TEMPLATE;
      previousRowCountRef.current = countContentRows(DEFAULT_TEMPLATE);
      wasAutoGeneratedRef.current = false;

      // ✅ Update the prop. The useEffect will run detection on the new JSON.
      setSampleResponse(pendingSampleValue);
    }

    setPendingSampleValue(null);
  };

  const handleCancelReset = () => {
    setShowResetConfirm(false);
    setPendingSampleValue(null);
  };

  // ============================================================
  // ✅ UPDATED: Handle raw template changes with tracking
  // Supports both old and new transformation formats
  // ============================================================
  const handleTemplateChange = (e) => {
    const newTemplate = e.target.value;
    const oldTemplate = previousTemplateRef.current;

    if (onTrackChange && oldTemplate && transformationRules.length > 0) {
      const oldRowCount = previousRowCountRef.current;
      const newRowCount = countContentRows(newTemplate);

      if (oldRowCount !== newRowCount) {
        console.log('[LeftPanel] 📊 Row count changed:', { oldRowCount, newRowCount });

        const oldLines = oldTemplate.split('\n');
        const newLines = newTemplate.split('\n');

        if (newRowCount > oldRowCount) {
          let addedIndex = -1;
          let addedContent = '';

          for (let i = 0; i < newLines.length; i++) {
            const newLine = newLines[i];
            const oldLine = oldLines[i] || '';
            if (newLine.trim() !== oldLine.trim() &&
              newLine.trim().length > 0 &&
              !newLine.trim().startsWith('{%') &&
              !newLine.trim().startsWith('{#')) {
              const existsInOld = oldLines.some(line => line.trim() === newLine.trim());
              if (!existsInOld) {
                addedIndex = i;
                addedContent = newLine;
                break;
              }
            }
          }

          if (addedIndex !== -1) {
            const selectorPath = generateSelectorPathForLine(newLines, addedIndex);
            console.log('[LeftPanel] ➕ Row added:', { addedContent, selectorPath });

            onTrackChange({
              type: 'add',
              selectorPath: selectorPath,
            });
          }
        } else if (newRowCount < oldRowCount) {
          let deletedIndex = -1;
          let deletedContent = '';

          for (let i = 0; i < oldLines.length; i++) {
            const oldLine = oldLines[i];
            const newLine = newLines[i] || '';
            if (oldLine.trim() !== newLine.trim() &&
              oldLine.trim().length > 0 &&
              !oldLine.trim().startsWith('{%') &&
              !oldLine.trim().startsWith('{#')) {
              const existsInNew = newLines.some(line => line.trim() === oldLine.trim());
              if (!existsInNew) {
                deletedIndex = i;
                deletedContent = oldLine;
                break;
              }
            }
          }

          if (deletedIndex !== -1) {
            const selectorPath = generateSelectorPathForLine(oldLines, deletedIndex);
            console.log('[LeftPanel] 🗑️ Row deleted:', { deletedContent, selectorPath });

            onTrackChange({
              type: 'delete',
              selectorPath: selectorPath,
            });
          }
        }
      }
    }

    // Update template
    setTemplate(newTemplate);
    previousTemplateRef.current = newTemplate;
    previousRowCountRef.current = countContentRows(newTemplate);

    if (wasAutoGeneratedRef.current) {
      wasAutoGeneratedRef.current = false;
    }
  };

  const isDefaultTemplate = templateString && templateString.trim() === DEFAULT_TEMPLATE;

  const toggleAdvanced = () => {
    setIsAdvancedOpen(!isAdvancedOpen);
  };

  const handleToggleEditMode = () => {
    if (!hasPreviewData) {
      console.log('[LeftPanel] ⏭️ Cannot toggle edit mode - no preview data');
      return;
    }

    console.log(`[LeftPanel] 🔘 Toggling edit mode: ${isEditMode ? "off" : "on"}`);

    if (!isEditMode) {
      if (onOpenEditor) {
        onOpenEditor();
      } else {
        setIsEditMode(true);
      }
    } else {
      if (onCloseEditor) {
        onCloseEditor();
      } else {
        setIsEditMode(false);
      }
    }
  };

  const hasSampleResponse = sampleResponse && sampleResponse.trim().length > 0;
  const isEditorDisabled = isDisabled || showPhaseWizard;

  // ============================================================
  // ✅ UX: Collapsed rail — when collapsed, only the toggle is
  // rendered so the panel becomes a thin strip. All business
  // logic (hooks, effects, handlers) still runs as normal.
  // ============================================================
  if (isCollapsed) {
    return (
      <div className={`${styles.leftPanel} ${styles.leftPanelCollapsed}`}>
        <button
          onClick={onToggleCollapse}
          className={styles.leftPanelToggle}
          title="Expand panel"
          aria-label="Expand panel"
        >
          ▶
        </button>

        {/* Keep LayoutChoicePopup mounted so nothing breaks if it's open */}
        <LayoutChoicePopup
          isOpen={showLayoutChoicePopup}
          onClose={handleCloseLayoutPopup}
          onConfirmSingleScreen={handleConfirmSingleScreen}
          onConfirmMultipleScreens={handleConfirmMultipleScreens}
          detectedArrays={detectedArraysForPopup}
          parsedResponse={pendingParsedResponse}
        />
      </div>
    );
  }

  return (
    <div className={styles.leftPanel}>

      {/* ✅ UX: Toggle button — pinned top-right of the expanded panel */}
      <button
        onClick={onToggleCollapse}
        className={styles.leftPanelToggle}
        title="Collapse panel"
        aria-label="Collapse panel"
      >
        ◀
      </button>

      {/* === STEP 3f: Info banner when user cancels layout popup === */}
      {layoutCancelNotice && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#ebf8ff',
            border: '1px solid #90cdf4',
            borderRadius: '8px',
            marginBottom: '12px',
            fontSize: '13px',
            color: '#2c5282',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <span style={{ fontSize: '18px' }}>ℹ️</span>
          <div>
            <strong>Defaulted to Single Screen.</strong> You can paste the
            response again to choose Multiple Screens instead.
          </div>
        </div>
      )}

      {!validation.valid && validation.errors && validation.errors.length > 0 && (
        <div className="validation-errors" style={{
          backgroundColor: '#fed7d7',
          border: '1px solid #fc8181',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '12px',
        }}>
          {validation.errors.map((error, i) => {
            const userFriendly = TemplateValidator.getUserFriendlyError(
              error,
              validation.loopInfo,
              validation.caseType
            );

            return (
              <div key={i} style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                fontSize: '13px',
                color: '#9b2c2c',
                marginBottom: i < validation.errors.length - 1 ? '12px' : '0',
                padding: '12px 16px',
                backgroundColor: 'rgba(255,255,255,0.6)',
                borderRadius: '8px',
                border: '1px solid rgba(252, 129, 129, 0.3)',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontWeight: '700',
                  fontSize: '15px',
                }}>
                  <span style={{ fontSize: '22px' }}>
                    {userFriendly.title.split(' ')[0] || '⚠️'}
                  </span>
                  <span>{userFriendly.title}</span>
                </div>

                <div style={{
                  paddingLeft: '4px',
                  lineHeight: '1.5'
                }}>
                  {userFriendly.message}
                </div>

                <div style={{
                  fontSize: '12px',
                  color: '#742a2a',
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  borderLeft: '3px solid #fc8181'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '2px' }}>📋 What's happening:</div>
                  <div>{userFriendly.context}</div>
                  {error.details && (
                    <div style={{ marginTop: '2px', opacity: 0.8 }}>📊 {error.details}</div>
                  )}
                </div>

                <div style={{
                  fontSize: '12px',
                  color: '#2b6cb0',
                  backgroundColor: 'rgba(235, 248, 255, 0.5)',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  borderLeft: '3px solid #4299e1'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '2px' }}>💡 How to fix:</div>
                  <div>{userFriendly.solution}</div>
                  {userFriendly.fix && (
                    <div style={{ marginTop: '2px', opacity: 0.8 }}>✏️ {userFriendly.fix}</div>
                  )}
                </div>

                {userFriendly.learnMore && (
                  <div style={{
                    fontSize: '11px',
                    color: '#4a5568',
                    backgroundColor: 'rgba(237, 242, 247, 0.5)',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    marginTop: '2px',
                    whiteSpace: 'pre-line',
                    fontFamily: 'monospace',
                  }}>
                    {userFriendly.learnMore}
                  </div>
                )}

                {error.visual && (
                  <div style={{
                    fontSize: '11px',
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    marginTop: '4px',
                    color: '#2d3748',
                    maxHeight: '150px',
                    overflow: 'auto'
                  }}>
                    {error.visual}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {validation.warnings && validation.warnings.length > 0 && (
        <div className="validation-warnings" style={{
          backgroundColor: '#fefcbf',
          border: '1px solid #f6ad55',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '12px',
        }}>
          {validation.warnings.map((warning, i) => (
            <div key={i} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              fontSize: '12px',
              color: '#744210',
              padding: '8px 12px',
              borderBottom: i < validation.warnings.length - 1 ? '1px solid rgba(246, 173, 85, 0.3)' : 'none',
              backgroundColor: 'rgba(255,255,255,0.4)',
              borderRadius: '4px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}>
                <span style={{ fontSize: '16px' }}>💡</span>
                <span>{warning.title || 'Suggestion'}</span>
              </div>
              <div style={{ paddingLeft: '28px' }}>
                <div>{warning.message}</div>
                {warning.context && (
                  <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '2px' }}>
                    {warning.context}
                  </div>
                )}
                {warning.solution && (
                  <div style={{
                    fontSize: '11px',
                    color: '#2b6cb0',
                    marginTop: '4px',
                    padding: '4px 8px',
                    backgroundColor: 'rgba(235, 248, 255, 0.5)',
                    borderRadius: '4px'
                  }}>
                    ✏️ {warning.solution}
                  </div>
                )}
                {warning.fix && (
                  <div style={{ fontSize: '11px', color: '#2b6cb0', marginTop: '2px' }}>
                    ✏️ {warning.fix}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(4px)',
          animation: 'fadeIn 0.3s ease',
        }}>
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; transform: scale(0.95); }
                to { opacity: 1; transform: scale(1); }
              }
              @keyframes pulse-warning {
                0% { box-shadow: 0 0 0 0 rgba(237, 137, 54, 0.4); }
                70% { box-shadow: 0 0 0 15px rgba(237, 137, 54, 0); }
                100% { box-shadow: 0 0 0 0 rgba(237, 137, 54, 0); }
              }
            `}
          </style>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px 40px',
            maxWidth: '480px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            position: 'relative',
          }}>
            <div style={{
              fontSize: '48px',
              textAlign: 'center',
              marginBottom: '12px',
              animation: 'pulse-warning 2s infinite',
            }}>
              ⚠️
            </div>
            <h2 style={{
              fontSize: '22px',
              fontWeight: '700',
              color: '#2d3748',
              textAlign: 'center',
              margin: '0 0 8px 0',
            }}>
              Reset Everything?
            </h2>
            <p style={{
              fontSize: '15px',
              color: '#4a5568',
              textAlign: 'center',
              lineHeight: '1.6',
              margin: '0 0 16px 0',
            }}>
              Changing the sample response will reset all your work:
            </p>
            <div style={{
              backgroundColor: '#fff5f5',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              border: '1px solid #fed7d7',
            }}>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                fontSize: '13px',
                color: '#4a5568',
                lineHeight: '1.8',
              }}>
                {transformationRules.length > 0 && <li>🗑️ All transformations will be removed</li>}
                {hasTemplateChanges && <li>📝 Template changes will be discarded</li>}
                {isEditMode && <li>✏️ Editor will be closed</li>}
                <li>🔄 Template will be regenerated from the new response</li>
              </ul>
            </div>
            <p style={{
              fontSize: '12px',
              color: '#e53e3e',
              textAlign: 'center',
              margin: '0 0 20px 0',
              fontWeight: '500',
            }}>
              This action cannot be undone!
            </p>
            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
            }}>
              <button
                onClick={handleCancelReset}
                style={{
                  padding: '10px 24px',
                  backgroundColor: 'transparent',
                  color: '#4a5568',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                  flex: 1,
                }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = '#f7fafc'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReset}
                style={{
                  padding: '10px 32px',
                  backgroundColor: '#e53e3e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  flex: 1,
                }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = '#c53030'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = '#e53e3e'; }}
              >
                Yes, Reset Everything
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sample Response Section */}
      <div className={styles.inputGroup}>
        <label className={styles.label}>
          Sample Response (JSON):
          <textarea
            id="sample-input"
            value={sampleResponse}
            onChange={handleSampleResponseChange}
            rows={hasSampleResponse ? 10 : 6}
            className={styles.textarea}
            placeholder='{"key": "value"}'
            style={{
              borderColor: hasSampleResponse ? '#e2e8f0' : '#fc8181',
              backgroundColor: hasSampleResponse ? 'white' : '#fff5f5',
            }}
          />
        </label>

        {!hasSampleResponse && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 14px',
            backgroundColor: '#ebf8ff',
            borderRadius: '8px',
            border: '1px solid #bee3f8',
            marginTop: '8px',
            fontSize: '13px',
            color: '#2b6cb0',
          }}>
            <span style={{ fontSize: '20px' }}>📋</span>
            <div>
              <div style={{ fontWeight: '600' }}>Paste your API response here</div>
              <div style={{ fontSize: '12px', color: '#4a5568' }}>
                Paste a JSON response from your API to start building your preview template.
              </div>
            </div>
          </div>
        )}

        {!hasSampleResponse && (
          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: '8px',
            flexWrap: 'wrap',
          }}>
            <button
              onClick={() => {
                const sample = `{
  "message": "Hello World",
  "data": {
    "id": 1,
    "name": "Example"
  }
}`;
                setSampleResponse(sample);
              }}
              style={{
                padding: '6px 14px',
                backgroundColor: '#ebf8ff',
                color: '#2b6cb0',
                border: '1px solid #bee3f8',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#bee3f8'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#ebf8ff'; }}
            >
              📝 Load Example
            </button>
            <button
              onClick={() => {
                const textarea = document.getElementById('sample-input');
                if (textarea) {
                  textarea.focus();
                  textarea.select();
                }
              }}
              style={{
                padding: '6px 14px',
                backgroundColor: 'transparent',
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#f7fafc'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; }}
            >
              📋 Focus & Paste
            </button>
          </div>
        )}

        {hasSampleResponse && (transformationRules.length > 0 || hasTemplateChanges) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '6px',
            fontSize: '11px',
            color: '#ed8936',
          }}>
            <span>🔄</span>
            <span>
              {transformationRules.length > 0 && `${transformationRules.length} transformation${transformationRules.length > 1 ? 's' : ''} applied`}
              {transformationRules.length > 0 && hasTemplateChanges && ' • '}
              {hasTemplateChanges && 'Unsaved template changes'}
              {' • '}
              <span style={{ color: '#718096' }}>
                Changing the response will reset everything
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Template Editor Section */}
      <div className={styles.inputGroup} style={{
        opacity: isEditorDisabled ? 0.5 : 1,
        pointerEvents: isEditorDisabled ? 'none' : 'auto',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}>
          <label className={styles.label} style={{ marginBottom: 0 }}>
            ✏️ Template Editor
          </label>
          <button
            id="edit-template-btn"
            onClick={handleToggleEditMode}
            disabled={isEditorDisabled}
            style={{
              padding: '4px 12px',
              backgroundColor: isEditMode ? '#fc8181' : (isEditorDisabled ? '#a0aec0' : '#4299e1'),
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isEditorDisabled ? 'not-allowed' : 'pointer',
              fontSize: '11px',
              fontWeight: '500',
              transition: 'all 0.2s',
              opacity: isEditorDisabled ? 0.6 : 1,
            }}
            title={isDisabled ? 'Please provide a sample response first' : (showPhaseWizard ? '✏️ Close wizard first' : '')}
          >
            {isEditMode ? '✕ Close Editor' : '✏️ Open Editor'}
          </button>
        </div>

        {isDisabled && !hasSampleResponse && (
          <div style={{
            padding: '10px 12px',
            backgroundColor: '#fefcbf',
            borderRadius: '4px',
            border: '1px solid #f6ad55',
            fontSize: '12px',
            color: '#744210',
            textAlign: 'center',
          }}>
            ⚡ Please paste a sample response above to enable the template editor.
          </div>
        )}

        {showPhaseWizard && !isDisabled && (
          <div style={{
            padding: '10px 12px',
            backgroundColor: '#fefcbf',
            borderRadius: '4px',
            border: '1px solid #f6ad55',
            fontSize: '12px',
            color: '#744210',
            textAlign: 'center',
          }}>
            ⚡ Template editor is disabled while wizard is active. Close the wizard first to edit.
          </div>
        )}

        {isEditMode && !isEditorDisabled && (
          <div style={{
            backgroundColor: '#f7fafc',
            borderRadius: '8px',
            border: '2px solid #4299e1',
            padding: '12px',
            marginTop: '8px',
            overflow: 'auto',
          }}>
            <TemplateEditor
              lines={templateLines}
              onLinesChange={setTemplateLines}
              isEditMode={isEditMode}
              onToggleEditMode={handleToggleEditMode}
              availableVariables={availableVariables}
              onTemplateUpdate={onTemplateUpdate}
              onTrackChange={onTrackChange}
              parsedSample={parsedSample}
              parseTemplateForEdit={parseTemplateForEdit}
              isCase2={isCase2}
              transformationSteps={transformationRules}
              onAddTransformationStep={onAddTransformationStep}
              onRemoveTransformationStep={onRemoveTransformationStep}
              onUpdateTransformationStep={onUpdateTransformationStep}
            />
          </div>
        )}

        {!isEditMode && !isEditorDisabled && (
          <div style={{
            padding: '10px 12px',
            backgroundColor: '#f7fafc',
            borderRadius: '4px',
            border: '1px dashed #cbd5e0',
            fontSize: '12px',
            color: '#718096',
            textAlign: 'center',
          }}>
            💡 Click "Open Editor" to edit your template with syntax highlighting and row management.
          </div>
        )}
      </div>

      {/* ============================================================
          ✅ UX: Advanced Section — hidden on production.
          On staging / dev, it still behaves as before.
          ============================================================ */}
      {!IS_PROD && (
        <div className={styles.inputGroup} style={{
          opacity: isEditorDisabled ? 0.5 : 1,
          pointerEvents: isEditorDisabled ? 'none' : 'auto',
        }}>
          <button
            onClick={toggleAdvanced}
            disabled={isEditorDisabled}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              backgroundColor: isAdvancedOpen ? '#edf2f7' : 'transparent',
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              cursor: isEditorDisabled ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              color: '#4a5568',
              width: '100%',
              transition: 'all 0.2s',
              opacity: isEditorDisabled ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isEditorDisabled) e.target.style.backgroundColor = '#edf2f7';
            }}
            onMouseLeave={(e) => {
              if (!isAdvancedOpen && !isEditorDisabled) e.target.style.backgroundColor = 'transparent';
            }}
            title={isDisabled ? 'Please provide a sample response first' : (showPhaseWizard ? '✏️ Close wizard first' : '')}
          >
            <span style={{ fontSize: '14px' }}>{isAdvancedOpen ? '▼' : '▶'}</span>
            <span>Advanced: Template (Nunjucks/Jinja)</span>
            {isDefaultTemplate && (
              <span style={{
                fontSize: '10px',
                color: '#718096',
                marginLeft: 'auto',
              }}>
                (Default)
              </span>
            )}
            {!isDefaultTemplate && templateString && (
              <span style={{
                fontSize: '10px',
                color: '#38a169',
                marginLeft: 'auto',
              }}>
                ✏️ Custom
              </span>
            )}
          </button>

          {isAdvancedOpen && !isEditorDisabled && (
            <div style={{ marginTop: '8px' }}>
              <div className={styles.fieldHelpers}>
                <span className={styles.helperText}>Available fields:</span>
                <div className={styles.fieldButtons}>
                  {Object.keys(flattenedFields).map((field) => (
                    <button
                      key={field}
                      onClick={() => insertField(field)}
                      className={styles.fieldButton}
                    >
                      {field}
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                id="template-input"
                value={templateString}
                onChange={handleTemplateChange}
                rows={6}
                className={styles.textarea}
                placeholder='{{ response_data.key }}'
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
              {isDefaultTemplate && parsedSample && (
                <div style={{
                  fontSize: '11px',
                  color: '#718096',
                  marginTop: '4px',
                  fontStyle: 'italic',
                }}>
                  💡 Using default template. Click "Render Preview" to auto-generate from sample data.
                </div>
              )}
              {!isDefaultTemplate && templateString && (
                <div style={{
                  fontSize: '11px',
                  color: '#38a169',
                  marginTop: '4px',
                  fontStyle: 'italic',
                }}>
                  ✏️ Custom template. Auto-generation is disabled. Edit freely.
                </div>
              )}
            </div>
          )}

          {isAdvancedOpen && isEditorDisabled && (
            <div style={{
              marginTop: '8px',
              padding: '10px 12px',
              backgroundColor: '#fefcbf',
              borderRadius: '4px',
              border: '1px solid #f6ad55',
              fontSize: '12px',
              color: '#744210',
              textAlign: 'center',
            }}>
              ⚡ {isDisabled ? 'Please paste a sample response above to enable the advanced template editor.' : 'Template editor is disabled while wizard is active. Close the wizard first to edit.'}
            </div>
          )}
        </div>
      )}

      {error && <div className={styles.errorMessage}>⚠️ {error}</div>}

      {/* Render Preview Button */}
      <button
        onClick={handleRender}
        disabled={!hasSampleResponse || isDisabled}
        className={styles.renderButton}
        style={{
          opacity: (!hasSampleResponse || isDisabled) ? 0.5 : 1,
          cursor: (!hasSampleResponse || isDisabled) ? 'not-allowed' : 'pointer',
        }}
        title={!hasSampleResponse ? 'Please provide a sample response first' : ''}
      >
        🔄 Render Preview
      </button>

      {/* ✅ UX: "Sample Data Structure" block removed — it was redundant. */}

      {/* ✅ Layout Choice Popup - for nested arrays */}
      <LayoutChoicePopup
        isOpen={showLayoutChoicePopup}
        onClose={handleCloseLayoutPopup}
        onConfirmSingleScreen={handleConfirmSingleScreen}
        onConfirmMultipleScreens={handleConfirmMultipleScreens}
        detectedArrays={detectedArraysForPopup}
        parsedResponse={pendingParsedResponse}
      />
    </div>
  );
};

export default LeftPanel;